// MarkdownRenderer.js
// Requires: micromark, micromark-extension-gfm, dompurify
// CSS: keep your existing MarkdownRenderer.scss classes (markdown-renderer, documentation-links, etc.)

import DOMPurify from "dompurify";
import { micromark } from "micromark";
import { gfm, gfmHtml } from "micromark-extension-gfm";
import { adobeDataLayerTrack } from "../../common/analytics";
import { getSelectedOrg } from "./support-org-util";

/**
 * @typedef {Object} MarkdownRendererOptions
 * @property {string} [className=""] Additional CSS classes
 * @property {"body"|"body-sm"|"body-xs"} [variant="body-sm"] Typography variant (maps to CSS classes)
 * @property {boolean} [allowExternalLinks=true] If true, links open in new tab with noopener
 * @property {string|number} [maxWidth="100%"] Max width for container
 * @property {string|number} [lineHeight=1.4] Line height for container
 * @property {any} [datapoint] Datapoint object/array/string used for template replacement
 * @property {any} [data] Insight payload `data` (stringified JSON or object) for `${codeKey}` replacement
 * @property {string} [insightCodeKey] Root key in parsed `data` matching placeholders `${insightCodeKey}` in content
 * @property {boolean} [allowDangerousHtml] When true, micromark passes block HTML through (then DOMPurify sanitizes). Use for API HTML like `<p>…</p>`.
 */

/* ---------------- helpers ---------------- */

/**
 * True for http(s) links whose origin differs from the current page (new tab / external).
 */
function isExternalHttpLink(href) {
    if (!href || href.startsWith("#")) return false;
    try {
        const url = new URL(href, window.location.href);
        if (url.protocol !== "http:" && url.protocol !== "https:") return false;
        return url.origin !== window.location.origin;
    } catch {
        return false;
    }
}

/**
 * Delegated click tracking for external links inside markdown HTML (listeners on in-memory
 * nodes are lost when {@link createMarkdownRendererHTML} serializes to a string). Call this
 * once on the container after its innerHTML is set.
 *
 * @param {HTMLElement} container
 * @param {{ companyEntitlements?: string, insightTitle?: string }} [options]
 */
export function attachMarkdownExternalLinkAnalytics(container, options = {}) {
    if (!container || container.__markdownExternalLinkAnalytics) return;

    const { companyEntitlements = "", insightTitle = "" } = options;

    const onClick = (ev) => {
        const anchor = ev.target && ev.target.closest && ev.target.closest("a[href]");
        if (!anchor) return;
        const href = anchor.getAttribute("href");
        if (!isExternalHttpLink(href)) return;

        const org = getSelectedOrg() || { name: "", id: "" };
        const title = (anchor.textContent || "").trim() || href;

        adobeDataLayerTrack({
            event: "linkClicked",
            eventType: "web.webInteraction.linkclicks",
            companyLoginDetails: {
                companyLoginName: org.name,
                companyLoginID: org.id,
                companyEntitlements
            },
            link: {
                linkTitle: title,
                linkLocation: insightTitle
                    ? `Support Insights: ${insightTitle}`
                    : "Support Insights",
                linkType: "external link"
            }
        });
    };

    container.addEventListener("click", onClick, true);
    container.__markdownExternalLinkAnalytics = true;
}

function escapeHtml(str) {
    return String(str)
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}

/**
 * Your incoming payload can be double-escaped, e.g.:
 *   "[{\\\"Hostname\\\":...}] }"
 * This normalizes it to valid JSON text:
 *   [{"Hostname":...}]
 */
function normalizePossiblyEscapedJsonString(s) {
    if (typeof s !== "string") return s;
    let t = s.trim();
    if (!t) return t;

    // unwrap if whole string is quoted once
    if (
        (t.startsWith('"') && t.endsWith('"')) ||
        (t.startsWith("'") && t.endsWith("'"))
    ) {
        t = t.slice(1, -1);
    }

    // Fix common double-escaping patterns
    t = t.replace(/\\\\\"/g, '"').replace(/\\"/g, '"'); // \" and \\\" => "
    t = t.replace(/\\\\\//g, "/"); // \\/ => /
    t = t.replace(/\\\\/g, "\\"); // \\ => \

    // Trim to the last valid JSON container end if payload has trailing junk
    const firstIdx = Math.min(
        ...["[", "{"]
            .map((ch) => t.indexOf(ch))
            .filter((i) => i >= 0)
    );

    if (Number.isFinite(firstIdx)) {
        const candidate = t.slice(firstIdx).trim();

        if (candidate.startsWith("[")) {
            const end = candidate.lastIndexOf("]");
            if (end !== -1) return candidate.slice(0, end + 1);
        }
        if (candidate.startsWith("{")) {
            const end = candidate.lastIndexOf("}");
            if (end !== -1) return candidate.slice(0, end + 1);
        }
        return candidate;
    }

    return t;
}

function tryParseJsonString(value) {
    if (typeof value !== "string") return { ok: false, value };
    const raw = value.trim();
    if (!raw) return { ok: false, value };

    const normalized = normalizePossiblyEscapedJsonString(raw);

    const looksJson =
        (normalized.startsWith("{") && normalized.endsWith("}")) ||
        (normalized.startsWith("[") && normalized.endsWith("]"));
    if (!looksJson) return { ok: false, value };

    try {
        return { ok: true, value: JSON.parse(normalized) };
    } catch {
        return { ok: false, value };
    }
}

function isPlainObject(val) {
    return Object.prototype.toString.call(val) === "[object Object]";
}

function escapeRegExp(str) {
    return String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** True when content likely mixes HTML from APIs (e.g. `<p>…</p>`) with markdown. */
function looksLikeMixedHtmlContent(s) {
    return /<\s*(p|div|h[1-6]|table|ul|ol|strong|em|br|span)\b/i.test(String(s || ""));
}

/** Leading spaces before `<` break HTML detection and stay visible; trim for API snippets. */
function normalizeLeadingHtmlWhitespace(s) {
    return String(s).replace(/^\uFEFF?\s+(?=<)/, "");
}

const RECOMMENDATION_SANITIZE = {
    ADD_TAGS: [
        "p",
        "div",
        "span",
        "strong",
        "em",
        "b",
        "i",
        "br",
        "ul",
        "ol",
        "li",
        "h2",
        "h3",
        "h4",
        "table",
        "thead",
        "tbody",
        "tr",
        "th",
        "td"
    ],
    ADD_ATTR: ["target", "rel", "colspan", "rowspan", "class", "role", "aria-label", "scope"],
    FORBID_TAGS: ["style", "script"],
    FORBID_ATTR: ["style"],
    ALLOW_DATA_ATTR: false,
    ADD_URI_SAFE_ATTR: ["target"]
};

/** Placeholder injected before micromark; replaced with HTML after sanitize (see injectInsightDataFragmentIntoDom). */
const INSIGHT_DATA_PLACEHOLDER = "[[INSIGHT_DATA_TABLE]]";

const INSIGHT_FRAGMENT_PURIFY = {
    ADD_TAGS: [
        "div",
        "table",
        "thead",
        "tbody",
        "tr",
        "th",
        "td",
        "span",
        "h4",
        "br",
        "em",
        "strong",
        "b",
        "i",
        "p",
        "ul",
        "ol",
        "li",
        "a"
    ],
    ADD_ATTR: ["class", "role", "aria-label", "colspan", "rowspan", "scope", "href", "target", "rel"],
    FORBID_TAGS: ["style", "script"],
    FORBID_ATTR: ["style"],
    ALLOW_DATA_ATTR: false
};

/**
 * Replace `${codeKeyName}` in content using parsed insight `data` from options.
 * Supports stringified JSON in `options.data` or `options.datapoint` (same shape as insight API `data`).
 *
 * @param {string} content Raw recommendation / markdown (may include `<p>${NewRelicFPCCheck}</p>`)
 * @param {{ data?: unknown, datapoint?: unknown }} options Pass `data` or reuse `datapoint` for the JSON payload
 * @param {string} codeKeyName Key on the parsed root object, e.g. `NewRelicFPCCheck` for `${NewRelicFPCCheck}`
 * @returns {{ content: string, fragmentHtml: string }} `fragmentHtml` is sanitized HTML injected post-markdown
 */
export function replaceInsightDataPlaceholders(content, options = {}, codeKeyName) {
    const outBase = String(content || "");
    if (!codeKeyName) {
        return { content: outBase, fragmentHtml: "" };
    }

    const raw =
        options && options.data !== undefined && options.data !== null
            ? options.data
            : options?.datapoint;

    const phRe = new RegExp(`\\$\\{${escapeRegExp(String(codeKeyName))}\\}`, "g");

    if (raw == null || raw === "") {
        return { content: outBase.replace(phRe, ""), fragmentHtml: "" };
    }

    const parsedRoot = tryParseJsonString(raw);
    const dpRoot = parsedRoot.ok ? parsedRoot.value : raw;
    const rootParsed = tryParseJsonString(dpRoot);
    const rootObj = rootParsed.ok ? rootParsed.value : dpRoot;

    if (!isPlainObject(rootObj)) {
        return { content: outBase.replace(phRe, ""), fragmentHtml: "" };
    }

    const slice = rootObj[codeKeyName];
    if (slice === undefined) {
        return { content: outBase.replace(phRe, ""), fragmentHtml: "" };
    }

    const fragmentHtml = buildInsightDataSliceHtml(slice);
    const replaced = outBase.replace(phRe, () => `\n\n${INSIGHT_DATA_PLACEHOLDER}\n\n`);

    return { content: replaced, fragmentHtml };
}

/** Column keys for FPC-style grouped rows → ALL CAPS header labels (design match). */
const INSIGHT_GROUPED_TABLE_COLUMNS = [
    { key: "Page Type", header: "PAGE TYPE" },
    { key: "Page Details", header: "PAGE DETAILS" },
    { key: "Full Page Cache (FPC) HIT Rate", header: "FULL PAGE CACHE (FPC) HIT RATE" }
];

function insightColumnHeaderLabel(key) {
    const def = INSIGHT_GROUPED_TABLE_COLUMNS.find((c) => c.key === key);
    return def ? def.header : String(key).toUpperCase();
}

function isInsightHitRateColumn(key) {
    const k = String(key);
    return (
        k === "Full Page Cache (FPC) HIT Rate" ||
        (/\bfpc\b/i.test(k) && /hit rate/i.test(k))
    );
}

/**
 * Collect table columns: known FPC order first, then any other keys on rows (stable union).
 */
function collectInsightGroupedColumns(rowObjects) {
    const seen = new Set();
    const cols = [];
    for (const { key } of INSIGHT_GROUPED_TABLE_COLUMNS) {
        if (rowObjects.some((row) => row && typeof row === "object" && key in row)) {
            cols.push(key);
            seen.add(key);
        }
    }
    for (const row of rowObjects) {
        if (!row || typeof row !== "object" || Array.isArray(row)) continue;
        for (const k of Object.keys(row)) {
            if (!seen.has(k)) {
                seen.add(k);
                cols.push(k);
            }
        }
    }
    return cols;
}

/**
 * One table: each top-level entry (Product, Category, …) is a row; child keys are columns.
 * Matches Support Insights FPC layout (PAGE TYPE | PAGE DETAILS | FPC HIT RATE).
 */
function buildGroupedSubObjectTable(slice) {
    const entries = Object.entries(slice);
    const rowObjects = entries.map(([, v]) => v).filter(Boolean);
    if (!rowObjects.length) return "";

    const columns = collectInsightGroupedColumns(rowObjects);
    if (!columns.length) return "";

    const thead = `<thead><tr>${columns
        .map((colKey) => {
            const cls = isInsightHitRateColumn(colKey) ? ' class="md-insight-col-hit-rate"' : "";
            return `<th scope="col"${cls}>${escapeHtml(insightColumnHeaderLabel(colKey))}</th>`;
        })
        .join("")}</tr></thead>`;

    const tbody = `<tbody>${rowObjects
        .map((row) => {
            if (!row || typeof row !== "object" || Array.isArray(row)) {
                return `<tr><td colspan="${columns.length}"><span class="md-injected-cell">${escapeHtml(
                    row == null ? "" : String(row)
                )}</span></td></tr>`;
            }
            return `<tr>${columns
                .map((colKey) => {
                    const v = row[colKey];
                    let inner;
                    if (v == null) {
                        inner = "";
                    } else if (typeof v === "string" && (v.includes("<") || v.includes("&lt;"))) {
                        inner = DOMPurify.sanitize(v, INSIGHT_FRAGMENT_PURIFY);
                    } else if (typeof v === "object") {
                        inner = `<span class="md-injected-cell">${escapeHtml(JSON.stringify(v))}</span>`;
                    } else {
                        inner = `<span class="md-injected-cell">${escapeHtml(String(v))}</span>`;
                    }
                    const tdCls = isInsightHitRateColumn(colKey) ? ' class="md-insight-cell-hit-rate"' : "";
                    return `<td${tdCls}>${inner}</td>`;
                })
                .join("")}</tr>`;
        })
        .join("")}</tbody>`;

    return `
      <div class="md-injected-table-wrap md-insight-data-table-wrap" role="region" aria-label="Insight metrics">
        <table class="md-injected-table md-insight-row-table">
          ${thead}
          ${tbody}
        </table>
      </div>
    `;
}

function buildKeyValueSectionHtml(title, obj) {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return "";

    const rows = Object.entries(obj)
        .map(([k, v]) => {
            let cellInner;
            if (v == null) {
                cellInner = "";
            } else if (typeof v === "string" && (v.includes("<") || v.includes("&lt;"))) {
                cellInner = DOMPurify.sanitize(v, INSIGHT_FRAGMENT_PURIFY);
            } else if (typeof v === "object") {
                cellInner = `<span class="md-injected-cell">${escapeHtml(JSON.stringify(v))}</span>`;
            } else {
                cellInner = `<span class="md-injected-cell">${escapeHtml(String(v))}</span>`;
            }
            return `<tr><th scope="row">${escapeHtml(k)}</th><td>${cellInner}</td></tr>`;
        })
        .join("");

    const h = title
        ? `<h4 class="md-insight-data-heading">${escapeHtml(title)}</h4>`
        : "";

    return `<div class="md-insight-data-section">${h}<div class="md-injected-table-wrap" role="region" aria-label="Insight data"><table class="md-injected-table"><tbody>${rows}</tbody></table></div></div>`;
}

function buildInsightDataSliceHtml(slice) {
    if (slice == null || slice === "") return "";

    if (typeof slice === "string") {
        const inner = DOMPurify.sanitize(slice, INSIGHT_FRAGMENT_PURIFY);
        return `<div class="md-insight-data-slice md-insight-data-html">${inner}</div>`;
    }

    if (Array.isArray(slice)) {
        return buildDatapointTable(slice);
    }

    if (!isPlainObject(slice)) {
        return `<div class="md-insight-data-slice"><span class="md-injected-cell">${escapeHtml(String(slice))}</span></div>`;
    }

    const entries = Object.entries(slice);
    const allSubObjects =
        entries.length > 0 &&
        entries.every(([, v]) => v && typeof v === "object" && !Array.isArray(v));

    if (allSubObjects) {
        return buildGroupedSubObjectTable(slice);
    }

    return buildKeyValueSectionHtml("", slice);
}

/**
 * Injects sanitized insight HTML for each {@link INSIGHT_DATA_PLACEHOLDER} token in text nodes.
 */
function injectInsightDataFragmentIntoDom(tempDiv, fragmentHtml) {
    if (!fragmentHtml) return;

    const safeHtml = DOMPurify.sanitize(fragmentHtml, INSIGHT_FRAGMENT_PURIFY);

    const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    for (const tn of textNodes) {
        let text = tn.nodeValue || "";
        if (!text.includes(INSIGHT_DATA_PLACEHOLDER)) continue;

        const frag = document.createDocumentFragment();
        let rest = text;

        while (rest.includes(INSIGHT_DATA_PLACEHOLDER)) {
            const parts = rest.split(INSIGHT_DATA_PLACEHOLDER);
            const before = parts.shift() ?? "";
            if (before) frag.appendChild(document.createTextNode(before));

            const wrapper = document.createElement("div");
            wrapper.innerHTML = safeHtml;
            while (wrapper.firstChild) frag.appendChild(wrapper.firstChild);

            rest = parts.join(INSIGHT_DATA_PLACEHOLDER);
        }

        if (rest) frag.appendChild(document.createTextNode(rest));
        tn.parentNode.replaceChild(frag, tn);
    }

    tempDiv.querySelectorAll("pre > code").forEach((code) => {
        const txt = code.textContent || "";
        if (!txt.includes(INSIGHT_DATA_PLACEHOLDER)) return;

        const pre = code.parentElement;
        let rest = txt;
        const container = document.createElement("div");

        while (rest.includes(INSIGHT_DATA_PLACEHOLDER)) {
            const parts = rest.split(INSIGHT_DATA_PLACEHOLDER);
            const before = parts.shift() ?? "";
            if (before.trim()) {
                const p = document.createElement("p");
                p.textContent = before;
                container.appendChild(p);
            }

            const wrapper = document.createElement("div");
            wrapper.innerHTML = safeHtml;
            while (wrapper.firstChild) container.appendChild(wrapper.firstChild);

            rest = parts.join(INSIGHT_DATA_PLACEHOLDER);
        }

        if (rest.trim()) {
            const p = document.createElement("p");
            p.textContent = rest;
            container.appendChild(p);
        }

        pre.replaceWith(container);
    });
}

/**
 * Resolve paths like:
 *  - datapoint.foo
 *  - datapoint[0]
 *  - datapoint[foo][bar]
 *  - datapoint.foo[0].bar
 * Unresolved -> undefined
 */
function resolveDatapointPath(datapointValue, pathExpr) {
    if (datapointValue == null) return undefined;

    const expr = String(pathExpr || "").trim();
    if (!expr.startsWith("datapoint")) return undefined;

    const tokens = [];
    let i = "datapoint".length;

    while (i < expr.length) {
        const ch = expr[i];

        if (ch === ".") {
            i++;
            let start = i;
            while (i < expr.length && /[A-Za-z0-9_$]/.test(expr[i])) i++;
            const key = expr.slice(start, i);
            if (!key) return undefined;
            tokens.push({ type: "key", value: key });
            continue;
        }

        if (ch === "[") {
            i++;
            let start = i;
            while (i < expr.length && expr[i] !== "]") i++;
            if (i >= expr.length) return undefined;

            let inside = expr.slice(start, i).trim();
            i++; // skip ]

            if (
                (inside.startsWith('"') && inside.endsWith('"')) ||
                (inside.startsWith("'") && inside.endsWith("'"))
            ) {
                inside = inside.slice(1, -1);
            }

            if (/^-?\d+$/.test(inside)) {
                tokens.push({ type: "index", value: Number(inside) });
            } else if (inside.length) {
                tokens.push({ type: "key", value: inside });
            } else {
                return undefined;
            }
            continue;
        }

        return undefined;
    }

    let cur = datapointValue;
    for (const t of tokens) {
        if (cur == null) return undefined;

        if (t.type === "index") {
            if (!Array.isArray(cur)) return undefined;
            cur = cur[t.value];
            continue;
        }

        if (typeof cur !== "object") return undefined;
        cur = cur[t.value];
    }
    return cur;
}

/**
 * Build a dynamic HTML table:
 * - Preferred headers appear first if present
 * - Remaining headers are appended dynamically (union of keys, stable order)
 */
function buildDatapointTable(arr, preferredHeaders = ["Hostname", "Table Name", "Dead tuples"]) {
    if (!Array.isArray(arr)) return "";

    const objects = arr.filter((x) => x && typeof x === "object" && !Array.isArray(x));

    if (objects.length) {
        const cols = [];
        const seen = new Set();

        for (const o of objects) {
            for (const k of Object.keys(o)) {
                if (!seen.has(k)) {
                    seen.add(k);
                    cols.push(k);
                }
            }
        }

        const preferred = preferredHeaders.filter((h) => seen.has(h));
        const rest = cols.filter((c) => !preferred.includes(c));
        const finalCols = [...preferred, ...rest];

        const thead = `<thead><tr>${finalCols
            .map((c) => `<th>${escapeHtml(c)}</th>`)
            .join("")}</tr></thead>`;

        const tbody = `<tbody>${arr
            .map((row) => {
                if (!row || typeof row !== "object" || Array.isArray(row)) {
                    return `<tr><td colspan="${finalCols.length}">${escapeHtml(
                        row == null ? "" : String(row)
                    )}</td></tr>`;
                }
                return `<tr>${finalCols
                    .map((c) => {
                        const v = row[c];
                        const cell = v && typeof v === "object" ? JSON.stringify(v) : v ?? "";
                        return `<td><span class="md-injected-cell">${escapeHtml(cell)}</span></td>`;
                    })
                    .join("")}</tr>`;
            })
            .join("")}</tbody>`;

        return `
      <div class="md-injected-table-wrap" role="region" aria-label="Datapoint table">
        <table class="md-injected-table">
          ${thead}
          ${tbody}
        </table>
      </div>
    `;
    }

    // Primitive array fallback
    return `
    <div class="md-injected-table-wrap" role="region" aria-label="Datapoint list">
      <table class="md-injected-table">
        <thead><tr><th>Value</th></tr></thead>
        <tbody>
          ${arr
            .map((v) => `<tr><td><span class="md-injected-cell">${escapeHtml(v ?? "")}</span></td></tr>`)
            .join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderValueForInjection(value) {
    if (value == null) return "";

    // Parse JSON string if possible (otherwise keep as-is)
    const parsed = tryParseJsonString(value);
    const v = parsed.ok ? parsed.value : value;

    // Objects/arrays render as code blocks for non-table cases
    if (Array.isArray(v) || isPlainObject(v)) {
        return "```json\n" + JSON.stringify(v, null, 2) + "\n```";
    }

    return String(v);
}

/**
 * Injects a sanitized table HTML where the placeholder appears in the final DOM.
 * This avoids Markdown turning HTML into &lt;table&gt; text or wrapping it into <pre><code>.
 */
function injectDatapointTableIntoDom(tempDiv, tableHtml) {
    if (!tableHtml) return;

    const safeTableHtml = DOMPurify.sanitize(tableHtml, {
        ADD_TAGS: ["div", "table", "thead", "tbody", "tr", "th", "td", "span"],
        ADD_ATTR: ["class", "role", "aria-label", "colspan", "rowspan"]
    });

    // Replace placeholder in text nodes (normal rendering)
    const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    for (const tn of textNodes) {
        const text = tn.nodeValue || "";
        if (!text.includes("[[DATAPOINT_TABLE]]")) continue;

        const parts = text.split("[[DATAPOINT_TABLE]]");
        const frag = document.createDocumentFragment();

        if (parts[0]) frag.appendChild(document.createTextNode(parts[0]));

        const wrapper = document.createElement("div");
        wrapper.innerHTML = safeTableHtml;
        while (wrapper.firstChild) frag.appendChild(wrapper.firstChild);

        if (parts[1]) frag.appendChild(document.createTextNode(parts[1]));

        tn.parentNode.replaceChild(frag, tn);
    }

    // Replace placeholder if Markdown wrapped it into <pre><code>...</code></pre>
    tempDiv.querySelectorAll("pre > code").forEach((code) => {
        const txt = code.textContent || "";
        if (!txt.includes("[[DATAPOINT_TABLE]]")) return;

        const pre = code.parentElement;
        const parts = txt.split("[[DATAPOINT_TABLE]]");

        const container = document.createElement("div");

        if (parts[0].trim()) {
            const p = document.createElement("p");
            p.textContent = parts[0];
            container.appendChild(p);
        }

        const wrapper = document.createElement("div");
        wrapper.innerHTML = safeTableHtml;
        while (wrapper.firstChild) container.appendChild(wrapper.firstChild);

        if (parts[1].trim()) {
            const p = document.createElement("p");
            p.textContent = parts[1];
            container.appendChild(p);
        }

        pre.replaceWith(container);
    });
}

/**
 * Replaces:
 * - {{datapoint}} / {{ datapoint }} / @{{ datapoint }}
 * - supports paths: {{datapoint.foo}}, {{datapoint[0]}}, {{datapoint[foo][bar]}}
 * - unresolved paths => ""
 *
 * Table behavior:
 * - If datapoint parses to an array (even if double-escaped), {{datapoint}} renders TABLE
 * - If datapoint parses to object and object.data parses to array, {{datapoint.data}} renders TABLE
 *
 * Returns { content, tableHtml } so we can inject table AFTER markdown processing.
 */
function replaceDatapointTokens(content, datapoint) {
    if (datapoint == null) {
        return {
            content: String(content || "")
                .replace(/@\{\{\s*datapoint[^}]*\s*\}\}/g, "")
                .replace(/\{\{\s*datapoint[^}]*\s*\}\}/g, ""),
            tableHtml: ""
        };
    }

    // Parse root if it is JSON (including double-escaped)
    const parsedRoot = tryParseJsonString(datapoint);
    const dpRoot = parsedRoot.ok ? parsedRoot.value : datapoint;

    // IMPORTANT: dpRoot might still be a JSON-looking string; parse again safely
    const rootParsed = tryParseJsonString(dpRoot);
    const rootValue = rootParsed.ok ? rootParsed.value : dpRoot;

    // Root array => table candidate
    const rootArray = Array.isArray(rootValue) ? rootValue : null;
    const tableFromRoot = rootArray ? buildDatapointTable(rootArray) : "";

    // object.data array => table candidate
    const dpData =
        rootValue && typeof rootValue === "object" && !Array.isArray(rootValue)
            ? rootValue.data
            : undefined;

    const dpDataParsed = tryParseJsonString(dpData);
    const dpDataValue = dpDataParsed.ok ? dpDataParsed.value : dpData;
    const tableFromData = Array.isArray(dpDataValue) ? buildDatapointTable(dpDataValue) : "";

    const tableHtml = tableFromRoot || tableFromData || "";

    function replaceOnePass(str, withAt) {
        const re = withAt
            ? /@\{\{\s*(datapoint[^}]*)\s*\}\}/g
            : /\{\{\s*(datapoint[^}]*)\s*\}\}/g;

        return str.replace(re, (_m, expr) => {
            const e = String(expr || "").trim();
            const normalized = e.replace(/\s+/g, "");

            // Root array => {{datapoint}} becomes placeholder
            if (tableFromRoot && normalized === "datapoint") {
                return "\n\n[[DATAPOINT_TABLE]]\n\n";
            }

            // object.data => {{datapoint.data}} becomes placeholder
            if (
                tableFromData &&
                (normalized === "datapoint.data" ||
                    normalized === "datapoint['data']" ||
                    normalized === 'datapoint["data"]' ||
                    normalized === "datapoint[data]")
            ) {
                return "\n\n[[DATAPOINT_TABLE]]\n\n";
            }

            const resolved =
                e === "datapoint" ? rootValue : resolveDatapointPath(rootValue, e);

            if (resolved === undefined || resolved === null) return "";

            return renderValueForInjection(resolved);
        });
    }

    let out = String(content || "");
    out = replaceOnePass(out, true);
    out = replaceOnePass(out, false);

    return { content: out, tableHtml };
}

/**
 * Create a DOM element that renders markdown safely.
 * @param {string} content Markdown string (may include {{datapoint...}} tokens)
 * @param {MarkdownRendererOptions} [options]
 * @returns {string} HTML string (container div)
 */
export function createMarkdownRendererHTML(content, options = {}) {
    const {
        className = "",
        variant = "body-sm",
        allowExternalLinks = true,
        maxWidth = "100%",
        lineHeight = 1.4,
        datapoint,
        insightCodeKey,
        allowDangerousHtml: allowDangerousHtmlOpt
    } = options;

    if (!content || content.trim() === "") return "";

    let preMarkdown = String(content);
    let insightFragmentHtml = "";
    if (insightCodeKey) {
        const insightPass = replaceInsightDataPlaceholders(preMarkdown, options, insightCodeKey);
        preMarkdown = insightPass.content;
        insightFragmentHtml = insightPass.fragmentHtml;
    }

    // 1) Replace datapoint tokens BEFORE markdown render
    const { content: templatedContent, tableHtml } = replaceDatapointTokens(
        preMarkdown,
        datapoint
    );

    // 2) → HTML: micromark+GFM still escapes inline `<p>…</p>` from APIs; when tags are present,
    //    sanitize the string as HTML instead (placeholders [[…]] are plain text and survive).
    const normalizedInput = normalizeLeadingHtmlWhitespace(templatedContent);

    let sanitized;
    if (looksLikeMixedHtmlContent(normalizedInput)) {
        sanitized = DOMPurify.sanitize(normalizedInput, RECOMMENDATION_SANITIZE);
    } else {
        const allowDangerousHtml =
            allowDangerousHtmlOpt === true || Boolean(insightCodeKey);

        const html = micromark(normalizedInput, {
            extensions: [gfm()],
            htmlExtensions: [gfmHtml({ allowDangerousHtml })]
        });

        sanitized = DOMPurify.sanitize(html, RECOMMENDATION_SANITIZE);
    }

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = sanitized;

    /* ---------- inject table after sanitize ---------- */
    injectDatapointTableIntoDom(tempDiv, tableHtml);
    injectInsightDataFragmentIntoDom(tempDiv, insightFragmentHtml);

    /* ---------- documentation list transform ---------- */
    const lists = tempDiv.querySelectorAll("ul, ol");
    lists.forEach((list) => {
        const listItems = Array.from(list.querySelectorAll("li"));
        if (!listItems.length) return;

        const isDocumentationList = listItems.every((li) => {
            const links = li.querySelectorAll("a");
            const textContent = (li.textContent || "").trim();
            const linkText = Array.from(links)
                .map((a) => (a.textContent || "").trim())
                .join(" ");

            return (
                links.length > 0 &&
                (textContent === linkText || textContent.length - linkText.length < 10)
            );
        });

        if (!isDocumentationList) return;

        const container = document.createElement("div");
        container.className = "documentation-links";

        listItems.forEach((li, index) => {
            li.querySelectorAll("a").forEach((link) => {
                const linkWrapper = document.createElement("div");
                linkWrapper.className =
                    index === 0 ? "documentation-link-first" : "documentation-link";

                const newLink = link.cloneNode(true);

                if (allowExternalLinks) {
                    newLink.setAttribute("target", "_blank");
                    newLink.setAttribute("rel", "noopener noreferrer");
                }

                linkWrapper.appendChild(newLink);
                container.appendChild(linkWrapper);
            });
        });

        list.replaceWith(container);
    });

    /* ---------- external links ---------- */
    if (allowExternalLinks) {
        tempDiv.querySelectorAll("a").forEach((a) => {
            a.setAttribute("target", "_blank");
            a.setAttribute("rel", "noopener noreferrer");
        });
    }

    const variantClass =
        variant === "body"
            ? "md-body"
            : variant === "body-xs"
                ? "md-body-xs"
                : "md-body-sm";

    return `
    <div
      class="markdown-renderer ${variantClass} ${className}"
      style="
        max-width: ${typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth};
        line-height: ${lineHeight};
      "
    >
      <div class="md-safe-wrap">
        ${tempDiv.innerHTML}
      </div>
    </div>
  `;
}

/* ✅ Recommended CSS (MarkdownRenderer.scss)
.md-safe-wrap { overflow-wrap: anywhere; word-break: break-word; }
.md-safe-wrap pre { max-width: 100%; overflow: auto; }
.md-injected-table-wrap { max-width: 100%; overflow: auto; border-radius: 8px; }
.md-injected-table { width: 100%; border-collapse: collapse; }
.md-injected-table th, .md-injected-table td { padding: 8px 10px; border: 1px solid rgba(0,0,0,0.12); vertical-align: top; }
.md-injected-cell { display: inline-block; max-width: 100%; overflow-wrap: anywhere; word-break: break-word; }
*/